import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Upload, Trash2, Save, ArrowLeft } from 'lucide-react';

function getApiBaseUrl() {
  return (
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api')
  );
}

function tryExtractSvgPreview(svgText) {
  const txt = (svgText ?? '').toString();
  const m = txt.match(/<svg\b[^>]*>([\s\S]*?)<\/svg>/i);
  if (!m) return null;
  const open = txt.match(/<svg\b([^>]*)>/i);
  const attrs = open ? open[1] : '';
  const vb = (attrs.match(/viewBox\s*=\s*(['"])(.*?)\1/i) || [])[2] || '0 0 100 100';
  return { viewBox: vb, body: m[1] || '' };
}

export default function Symbols() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [symbols, setSymbols] = useState([]);
  const [error, setError] = useState('');
  const [croppingId, setCroppingId] = useState(null);

  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [defaultSizeIn, setDefaultSizeIn] = useState('0.5');
  const [svgText, setSvgText] = useState('');
  const [saving, setSaving] = useState(false);

  const svgPreview = useMemo(() => tryExtractSvgPreview(svgText), [svgText]);

  const loadSymbols = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiBaseUrl()}/designer-symbols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Failed to load symbols');
      setSymbols(Array.isArray(data.symbols) ? data.symbols : []);
    } catch (e) {
      console.error('loadSymbols error:', e);
      setError(e.message || 'Failed to load symbols');
      setSymbols([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSymbols();
  }, []);

  const handleUploadSvg = async (file) => {
    try {
      if (!file) return;
      const text = await file.text();
      setSvgText(text);
      if (!label.trim()) setLabel(file.name.replace(/\.svg$/i, ''));
      if (!slug.trim()) {
        const base = file.name.replace(/\.svg$/i, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        setSlug(base.slice(0, 40));
      }
    } catch (e) {
      console.error('handleUploadSvg error:', e);
      alert('Failed to read SVG file');
    }
  };

  const createSymbol = async () => {
    if (saving) return;
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiBaseUrl()}/designer-symbols`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          slug: slug.trim(),
          label: label.trim(),
          svg: svgText,
          default_size_in: defaultSizeIn
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Failed to create symbol');
      setSlug('');
      setLabel('');
      setSvgText('');
      setDefaultSizeIn('0.5');
      await loadSymbols();
    } catch (e) {
      console.error('createSymbol error:', e);
      setError(e.message || 'Failed to create symbol');
    } finally {
      setSaving(false);
    }
  };

  const deleteSymbol = async (id) => {
    if (!window.confirm('Delete this symbol?')) return;
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiBaseUrl()}/designer-symbols/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Failed to delete symbol');
      await loadSymbols();
    } catch (e) {
      console.error('deleteSymbol error:', e);
      alert(e.message || 'Failed to delete symbol');
    }
  };

  const tightenSymbolViewBox = async (symbol) => {
    if (!symbol?.id) return;
    if (croppingId) return;
    setCroppingId(symbol.id);
    try {
      const vb = (symbol.svg_viewbox || '0 0 100 100').toString();
      const body = (symbol.svg_body || '').toString();
      if (!body.trim()) throw new Error('Symbol has no SVG body to crop');

      // Create an offscreen SVG, render the body, measure its bounds, and compute a tight viewBox.
      const ns = 'http://www.w3.org/2000/svg';
      const wrap = document.createElement('div');
      wrap.style.position = 'absolute';
      wrap.style.left = '-10000px';
      wrap.style.top = '-10000px';
      wrap.style.width = '0';
      wrap.style.height = '0';
      wrap.style.overflow = 'hidden';

      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', vb);
      svg.setAttribute('width', '200');
      svg.setAttribute('height', '200');

      const g = document.createElementNS(ns, 'g');
      // Safe enough: the server already sanitizes svg_body; this is only used for measurement.
      g.innerHTML = body;
      svg.appendChild(g);
      wrap.appendChild(svg);
      document.body.appendChild(wrap);

      let bbox;
      try {
        bbox = g.getBBox();
      } finally {
        document.body.removeChild(wrap);
      }

      if (!bbox || !Number.isFinite(bbox.x) || !Number.isFinite(bbox.y) || !Number.isFinite(bbox.width) || !Number.isFinite(bbox.height)) {
        throw new Error('Could not measure symbol bounds');
      }
      if (bbox.width <= 0 || bbox.height <= 0) {
        throw new Error('Measured empty symbol bounds');
      }

      const pad = Math.max(0.5, Math.max(bbox.width, bbox.height) * 0.05);
      const x = bbox.x - pad;
      const y = bbox.y - pad;
      const w = bbox.width + pad * 2;
      const h = bbox.height + pad * 2;
      const fmt = (n) => {
        const v = Math.round(Number(n) * 1000) / 1000;
        return String(v);
      };
      const nextViewBox = `${fmt(x)} ${fmt(y)} ${fmt(w)} ${fmt(h)}`;

      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiBaseUrl()}/designer-symbols/${encodeURIComponent(symbol.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ svg_viewbox: nextViewBox })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Failed to crop symbol');

      await loadSymbols();
    } catch (e) {
      console.error('tightenSymbolViewBox error:', e);
      alert(e?.message || 'Failed to crop symbol');
    } finally {
      setCroppingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="min-w-0">
            <div className="text-xl font-bold text-gray-900">Symbols</div>
            <div className="text-xs text-gray-500">Manage your custom symbol library (SVG)</div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-bold text-gray-900">Create symbol</div>
              <button
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Clear"
                onClick={() => {
                  setSlug('');
                  setLabel('');
                  setSvgText('');
                  setDefaultSizeIn('0.5');
                }}
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Slug (id)</label>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g. dt_alt"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Label</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g. Deciduous Tree (Alt)"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Default size (in)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={defaultSizeIn}
                    onChange={(e) => setDefaultSizeIn(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Upload SVG</label>
                  <label className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 cursor-pointer">
                    <Upload className="w-4 h-4 text-gray-700" />
                    Choose file
                    <input
                      type="file"
                      accept=".svg,image/svg+xml"
                      className="hidden"
                      onChange={(e) => handleUploadSvg(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">SVG (paste)</label>
                <textarea
                  value={svgText}
                  onChange={(e) => setSvgText(e.target.value)}
                  className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono"
                  placeholder="<svg ...>...</svg>"
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl border border-gray-200 bg-white flex items-center justify-center">
                    {svgPreview ? (
                      <svg viewBox={svgPreview.viewBox} className="w-14 h-14">
                        <g dangerouslySetInnerHTML={{ __html: svgPreview.body }} />
                      </svg>
                    ) : (
                      <div className="text-[11px] text-gray-400 text-center px-2">Preview</div>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Tip: use a `viewBox` around a 100×100 grid for best results.
                  </div>
                </div>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 disabled:bg-gray-300"
                  onClick={createSymbol}
                  disabled={saving || !slug.trim() || !label.trim() || !svgText.trim()}
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Create'}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-bold text-gray-900">Your symbols</div>
              <button
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
                onClick={loadSymbols}
                disabled={loading}
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : symbols.length === 0 ? (
              <div className="text-sm text-gray-500">No custom symbols yet.</div>
            ) : (
              <div className="space-y-3">
                {symbols.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200">
                    <div className="w-12 h-12 rounded-xl border border-gray-200 bg-white flex items-center justify-center">
                      <svg viewBox={s.svg_viewbox || '0 0 100 100'} className="w-10 h-10">
                        <g dangerouslySetInnerHTML={{ __html: s.svg_body || '' }} />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-gray-900 truncate">{s.label}</div>
                      <div className="text-xs text-gray-500 truncate">`{s.slug}`</div>
                    </div>
                    <button
                      className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      title="Crop whitespace by tightening the SVG viewBox"
                      disabled={Boolean(croppingId)}
                      onClick={() => tightenSymbolViewBox(s)}
                    >
                      {croppingId === s.id ? 'Cropping…' : 'Crop'}
                    </button>
                    <button
                      className="p-2 rounded-lg hover:bg-red-50"
                      title="Delete"
                      onClick={() => deleteSymbol(s.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

